import {useEffect,useState, type ImgHTMLAttributes} from 'react';

/**
 * Preserve the approved character artwork while removing only connected,
 * near-white exterior pixels. Interior white robot details are not replaced.
 * Images are decoded once per source on this origin and the transparent
 * results are shared by profile cards, the studio preview and the gallery.
 */
const processed = new Map<string, Promise<string>>();
let workQueue: Promise<void> = Promise.resolve();

function prepare(src: string): Promise<string> {
  if (!/\/(?:agentordinary-v7\/ao-\d+|default-agent-(?:female|male))\.webp(?:$|\?)/.test(src)
      && !/^\/assets\/agentordinary\/(features|more)\/[a-z0-9_/-]+\.webp(?:$|\?)/i.test(src)) {
    return Promise.resolve(src);
  }
  const existing = processed.get(src);
  if (existing) return existing;

  const current = workQueue.then(() => new Promise<string>((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const width = image.naturalWidth, height = image.naturalHeight;
        if (!width || !height || width * height > 5_000_000) { resolve(src); return; }
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext('2d', {willReadFrequently: true});
        if (!ctx) { resolve(src); return; }
        ctx.drawImage(image, 0, 0);
        const pixels = ctx.getImageData(0, 0, width, height);
        const rgba = pixels.data, total = width * height;
        // Original transparent artwork must remain exactly as supplied.
        if (rgba[3] < 240 || rgba[(width-1)*4+3] < 240
          || rgba[(total-width)*4+3] < 240 || rgba[(total-1)*4+3] < 240) {
          resolve(src); return;
        }
        const visited = new Uint8Array(total);
        const stack = new Int32Array(total);
        let count = 0, changed = 0;
        const isExterior = (i: number) => {
          const p = i * 4;
          const red = rgba[p], green = rgba[p+1], blue = rgba[p+2];
          return red >= 232 && green >= 232 && blue >= 232
            && Math.max(red, green, blue) - Math.min(red, green, blue) <= 30;
        };
        const enqueue = (i: number) => {
          if (!visited[i]) { visited[i] = 1; if (isExterior(i)) stack[count++] = i; }
        };
        for (let x=0; x<width; x++) { enqueue(x); enqueue((height-1)*width+x); }
        for (let y=1; y<height-1; y++) { enqueue(y*width); enqueue(y*width+width-1); }
        while (count) {
          const i=stack[--count];
          rgba[i*4+3]=0; changed++;
          const x=i%width, y=(i/width)|0;
          if (x>0) enqueue(i-1);
          if (x+1<width) enqueue(i+1);
          if (y>0) enqueue(i-width);
          if (y+1<height) enqueue(i+width);
        }
        if (!changed || changed < total * .015) { resolve(src); return; }
        // Feather only the remaining immediate fringe, never enclosed white art.
        for (let y=1; y<height-1; y++) {
          for (let x=1; x<width-1; x++) {
            const i=y*width+x, p=i*4;
            if (rgba[p+3]!==255) continue;
            const light=Math.min(rgba[p],rgba[p+1],rgba[p+2]);
            if (light<210) continue;
            if (rgba[p-1]===0 || rgba[p+7]===0 || rgba[p-width*4+3]===0 || rgba[p+width*4+3]===0) {
              rgba[p+3]=Math.max(0,Math.min(255,Math.round((248-light)*7)));
            }
          }
        }
        ctx.putImageData(pixels,0,0);
        canvas.toBlob(blob => blob ? resolve(URL.createObjectURL(blob)) : resolve(src), 'image/webp', .94);
      } catch (cause) { reject(cause); }
    };
    image.onerror = () => reject(new Error('Character source unavailable'));
    image.src = src;
  }));
  workQueue = current.then(()=>{},()=>{});
  processed.set(src,current);
  return current;
}

type Props = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> & {
  src: string;
  fallbackSrc?: string;
};
export default function TransparentCharacterImage({src,fallbackSrc,style,onError,...rest}: Props) {
  const [source,setSource] = useState(src);
  const [ready,setReady] = useState(false);
  useEffect(() => {
    let active = true;
    setSource(src); setReady(false);
    prepare(src).then(result => {
      if(active) {setSource(result);setReady(true);}
    }).catch(() => {
      if(!active) return;
      if(fallbackSrc && fallbackSrc !== src) {
        prepare(fallbackSrc).then(result => {
          if(active) {setSource(result);setReady(true);}
        }).catch(()=>{if(active) {setSource(fallbackSrc);setReady(true);}});
      } else {setSource(src);setReady(true);}
    });
    return () => {active=false;};
  },[src,fallbackSrc]);
  return <img {...rest} src={source} style={{...style,opacity:ready?1:0,transition:'opacity .15s ease'}}
    onError={event=>{if(fallbackSrc && source!==fallbackSrc) {setSource(fallbackSrc);setReady(true);} onError?.(event);}}/>;
}
